import { Link } from "react-router-dom";
import { Instagram, Music, Phone, MapPin } from "lucide-react";
import { useLang } from "../lib/LanguageContext";
import { BRAND } from "../lib/menuData";

export const Footer = () => {
  const { t } = useLang();

  return (
    <footer
      data-testid="footer"
      className="relative bg-brand-ink text-brand-cream/85 pt-20 pb-10 overflow-hidden"
    >
      <div className="absolute top-0 right-1/4 w-[400px] h-[400px] rounded-full bg-brand-blue/10 blur-[120px] pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="grid md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={BRAND.logo}
                alt="Hayalan Coffee"
                className="w-12 h-12 rounded-full object-cover ring-1 ring-brand-cream/15"
              />
              <div className="flex flex-col leading-none">
                <span className="font-serif text-2xl text-brand-cream tracking-tight">
                  Hayalan
                </span>
                <span className="text-[10px] uppercase tracking-[0.32em] text-brand-gold mt-0.5">
                  Coffee
                </span>
              </div>
            </div>
            <p className="mt-6 text-sm leading-relaxed text-brand-cream/65 max-w-sm">
              {t.footer.tagline}
            </p>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.28em] text-brand-gold font-medium">
              {t.footer.quick}
            </h4>
            <ul className="mt-5 space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-sm hover:text-brand-cream transition-colors"
                >
                  {t.nav.home}
                </Link>
              </li>
              <li>
                <a
                  href="/#about"
                  className="text-sm hover:text-brand-cream transition-colors"
                >
                  {t.nav.about}
                </a>
              </li>
              <li>
                <Link
                  to="/menu"
                  className="text-sm hover:text-brand-cream transition-colors"
                >
                  {t.nav.menu}
                </Link>
              </li>
              <li>
                <a
                  href="/#gallery"
                  className="text-sm hover:text-brand-cream transition-colors"
                >
                  {t.nav.gallery}
                </a>
              </li>
              <li>
                <a
                  href="/#contact"
                  className="text-sm hover:text-brand-cream transition-colors"
                >
                  {t.nav.contact}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-xs uppercase tracking-[0.28em] text-brand-gold font-medium">
              {t.footer.contact}
            </h4>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex gap-3">
                <MapPin
                  className="w-4 h-4 mt-0.5 flex-shrink-0 text-brand-blue"
                  strokeWidth={1.8}
                />
                <span className="text-brand-cream/75 leading-relaxed">
                  {BRAND.address}
                </span>
              </li>
              <li>
                <a
                  href={`tel:${BRAND.phone}`}
                  className="flex gap-3 hover:text-brand-cream transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone
                    className="w-4 h-4 mt-0.5 flex-shrink-0 text-brand-blue"
                    strokeWidth={1.8}
                  />
                  <span>{BRAND.phoneDisplay}</span>
                </a>
              </li>
            </ul>
            <h4 className="mt-8 text-xs uppercase tracking-[0.28em] text-brand-gold font-medium">
              {t.footer.follow}
            </h4>
            <div className="mt-4 flex gap-3">
              <a
                href={BRAND.ig}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                aria-label="Instagram"
                className="w-10 h-10 rounded-full bg-brand-cream/8 hover:bg-brand-blue flex items-center justify-center transition-colors border border-brand-cream/10"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.8} />
              </a>
              <a
                href={BRAND.tiktok}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-tiktok"
                aria-label="TikTok"
                className="w-10 h-10 rounded-full bg-brand-cream/8 hover:bg-brand-blue flex items-center justify-center transition-colors border border-brand-cream/10"
              >
                <Music className="w-4 h-4" strokeWidth={1.8} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-brand-cream/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-brand-cream/55">
            © {new Date().getFullYear()} Hayalan Coffee. {t.footer.rights}
          </p>
          <p className="text-xs text-brand-cream/55 font-serif italic">
            Pangkalpinang, Bangka Belitung
          </p>
        </div>
      </div>
    </footer>
  );
};
